import { createTheme, type Theme } from '@mui/material/styles';
// Registers the picker components on the theme's `components` map.
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { derivePalette } from './derivePalette';
import { DEFAULT_PRESET, type ThemePreset } from './presets';
import { inputAutofillAnimation } from './inputAutofill';

export const SIDEBAR_WIDTH = 256;
export const SIDEBAR_MINI_WIDTH = 64;
export const ASIDE_WIDTH = 360;
export const HEADER_HEIGHT = 56;
/**
 * Icon size for the shell chrome (header actions, sidebar nav). Smaller than MUI's 24px default:
 * these are navigation furniture and shouldn't carry as much weight as page content. Outlined
 * icons stroke proportionally, so a smaller glyph is also a thinner one — ~1.5px here against
 * 2px at the default size. That is the only weight lever @mui/icons-material offers; its glyphs
 * are fixed-weight filled paths with no `wght` axis. 18px is the floor: the mini sidebar is
 * icon-only at 64px wide, and smaller glyphs stop being scannable there.
 *
 * In rem (18/16) rather than px so the user's font-size preset (see `fontSize.ts`) scales the
 * chrome along with the page content.
 */
export const SHELL_ICON_SIZE = '1.125rem';

/** Stacking contract while Bootstrap is still loaded (Bootstrap modal = 1050/1055). */
export const zuulZIndex = {
  appBar: 1020,
  drawer: 1035,
  fab: 1040,
  legacyBootstrapModal: 1055,
  /** Draggable preview windows: over the shell and sticky bars, under dialogs and every popper. */
  floatingWindow: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const;

const BUTTON_ICON_SIZE_VARIANTS = [
  { props: { size: 'small' as const }, style: { '& > *:nth-of-type(1)': { fontSize: '1.125rem' } } },
  { props: { size: 'medium' as const }, style: { '& > *:nth-of-type(1)': { fontSize: '1.25rem' } } },
  { props: { size: 'large' as const }, style: { '& > *:nth-of-type(1)': { fontSize: '1.375rem' } } },
];

const FONT_STACK = [
  '"Inter Variable"',
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'sans-serif',
].join(',');

/**
 * Builds the app theme for a color preset. Both color schemes come from `derivePalette`, so the
 * preset drives backgrounds, text, dividers and interaction states as well as primary/secondary —
 * everything MUI components read from the palette. Only the non-color parts are shared verbatim.
 */
function buildAppTheme(preset: ThemePreset): Theme {
  return createTheme({
    cssVariables: {
      // MUI writes data-bs-theme="light|dark" on <html>; legacy CSS keyed on [data-bs-theme] keeps working.
      colorSchemeSelector: 'data-bs-theme',
      cssVarPrefix: 'mui',
    },
    colorSchemes: {
      light: { palette: derivePalette(preset.light, 'light') },
      dark: { palette: derivePalette(preset.dark, 'dark') },
    },
    shape: { borderRadius: 8 },
    spacing: 8,
    typography: {
      fontFamily: FONT_STACK,
      fontSize: 14,
      h1: { fontSize: '2rem', fontWeight: 600 },
      h2: { fontSize: '1.5rem', fontWeight: 600 },
      h3: { fontSize: '1.25rem', fontWeight: 600 },
      h4: { fontSize: '1.125rem', fontWeight: 600 },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    zIndex: {
      appBar: zuulZIndex.appBar,
      drawer: zuulZIndex.drawer,
      fab: zuulZIndex.fab,
      modal: zuulZIndex.modal,
      snackbar: zuulZIndex.snackbar,
      tooltip: zuulZIndex.tooltip,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Themed scrollbars: a transparent track lets each scroll container keep its own
            // background (the stock near-white track clashed with the tinted surfaces), and the
            // thumb reuses the derived divider tint. `scrollbar-color` is inherited, so this one
            // declaration covers the page, the sidebar nav, editors — every scroller.
            scrollbarColor: 'var(--mui-palette-divider) transparent',
          },
        },
      },
      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        // MUI sizes a button's start/end icon in px (18/20/22) as size variants. Restated in rem
        // so the icon follows the font-size preset the way the label already does.
        styleOverrides: {
          startIcon: { variants: BUTTON_ICON_SIZE_VARIANTS },
          endIcon: { variants: BUTTON_ICON_SIZE_VARIANTS },
        },
      },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      // Autofill keyframes are injected once by AppThemeProvider rather than per input; see
      // ./inputAutofill for why (a big criteria screen froze on every tab switch).
      MuiInputBase: {
        defaultProps: { disableInjectingGlobalStyles: true },
        styleOverrides: { input: inputAutofillAnimation },
      },
      // The date pickers render MuiPickersTextField, not MuiTextField, so they need the same
      // defaults spelled out or they sit short and tall beside every other field on a form.
      MuiPickersTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiTable: { defaultProps: { size: 'small' } },
      MuiCard: { defaultProps: { variant: 'outlined' } },
      // The tinted band + rule turns every card header into a visible section marker; without it a
      // busy screen is a stack of identical white slabs whose titles float in the body text.
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: (t.vars ?? t).palette.zuul.sectionHeader,
            borderBottom: `1px solid ${(t.vars ?? t).palette.divider}`,
            padding: '10px 16px',
          }),
          title: { fontSize: '1rem', fontWeight: 600 },
        },
      },
      // Footer strip: the action row zones itself off the card body the same way the header does.
      MuiCardActions: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: (t.vars ?? t).palette.zuul.inset,
            borderTop: `1px solid ${(t.vars ?? t).palette.divider}`,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme: t }) => ({
            backgroundColor: (t.vars ?? t).palette.zuul.inset,
            fontWeight: 600,
          }),
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiTooltip: { defaultProps: { arrow: true } },
      // Dialogs open from the top of the viewport rather than vertically centred, so a
      // dialog's header lands in the same place whatever its height. Set on the container
      // rather than in the @/ui Modal so a plain MUI Dialog behaves the same way.
      MuiDialog: { styleOverrides: { container: { alignItems: 'flex-start' } } },
      // Dialogs get the same header band as cards, so a modal reads as one more section.
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: (t.vars ?? t).palette.zuul.sectionHeader,
            borderBottom: `1px solid ${(t.vars ?? t).palette.divider}`,
            paddingTop: 12,
            paddingBottom: 12,
          }),
        },
      },
      // MUI zeroes a dialog body's top padding when it follows a title. That clips the
      // floating label of an outlined field placed first in the body, which most of the
      // converted forms do. Give it back room — a little more now that the title is a band.
      MuiDialogContent: {
        styleOverrides: { root: { '.MuiDialogTitle-root + &': { paddingTop: 16 } } },
      },
    },
  });
}

const themeCache = new Map<string, Theme>();

/**
 * The theme for a preset. Cached per preset id so switching back and forth reuses the same object
 * and React can bail out of re-rendering subtrees that only depend on theme identity.
 */
export function createAppTheme(preset: ThemePreset = DEFAULT_PRESET): Theme {
  const cached = themeCache.get(preset.id);
  if (cached) return cached;
  const built = buildAppTheme(preset);
  themeCache.set(preset.id, built);
  return built;
}

/** The default-preset theme (used by tests and as the initial theme). */
const theme = createAppTheme();

export default theme;
